// pages/Home.js
import React, { useState, useEffect } from "react";
import { Row, Col, Card, Statistic, Switch, message, Select, Spin } from "antd";
import { DatabaseOutlined, FileTextOutlined, SyncOutlined } from "@ant-design/icons";
import PageTitle from "../components/PageTitle";
import api from "../services/api";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A020F0', '#FF1493'];

const Home = () => {
  const [sourcesCount, setSourcesCount] = useState(0);
  const [newsCount, setNewsCount] = useState(0);
  const [engineRunning, setEngineRunning] = useState(false);
  const [loading, setLoading] = useState(false);

  const [categoryStats, setCategoryStats] = useState([]);
  const [dailyStats, setDailyStats] = useState([]);
  const [daysRange, setDaysRange] = useState(7);
  const [summary, setSummary] = useState({ total: 0, average: 0 });
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState({ total: 0, average: 0 });
  const [monthsRange, setMonthsRange] = useState(12);

  const [loadingCharts, setLoadingCharts] = useState(false);

  
  
  // ----- فراخوانی داشبورد کلی -----
  const fetchDashboard = async () => {
    try {
      const res = await api.get("/dashboard/dashboard");
      setSourcesCount(res.data.sourceCount || 0);
      setNewsCount(res.data.newsCount || 0);
      setEngineRunning(res.data.engineStatus || false);
      setCategoryStats(res.data.categoryStats || []);
    } catch (err) {
      console.error("خطا در دریافت داشبورد:", err);
      message.error("خطا در بارگذاری اطلاعات داشبورد");
    }
  };

  // ----- فراخوانی آمار روزانه -----
  const fetchDailyStats = async (days = 7) => {
    try {
      setLoadingCharts(true);
      const res = await api.get(`/dashboard/daily-news-stats?days=${days}`);
      console.log("my test:", res);
      setDailyStats(res.data.daily || []);
      setSummary({
        total: res.data.totalCount || 0,
        average: res.data.averagePerDay || 0
      });
    } catch (err) {
      console.error("خطا در آمار روزانه:", err);
    } finally {
      setLoadingCharts(false);
    }
  };

  // ----- فراخوانی آمار ماهانه -----
  const fetchMonthlyStats = async (months = 12) => {
    try {
      setLoadingCharts(true);
      const res = await api.get(`/dashboard/monthly-news-stats?months=${months}`);
      const data = res.data.monthly || [];
      setMonthlyStats(data);
  
      const total = data.reduce((sum, item) => sum + item.count, 0);
      const average = data.length ? Math.round(total / data.length) : 0;
      setMonthlySummary({ total, average });
    } catch (err) {
      console.error("خطا در آمار ماهانه:", err);
    } finally {
      setLoadingCharts(false);
    }
  };
  
  
  

  const handleDaysChange = (value) => {
    setDaysRange(value);
    fetchDailyStats(value);
    fetchMonthlyStats();
  };

  const handleMonthsChange = (value) => {
    setMonthsRange(value);
    fetchMonthlyStats(value);
  };

  const toggleEngine = async (checked) => {
    setLoading(true);
    try {
      await api.post("/dashboard/status", { status: checked });
      setEngineRunning(checked);
      message.success(`Engine ${checked ? "شروع شد" : "متوقف شد"}`);
    } catch (err) {
      console.error("خطا در تغییر وضعیت موتور:", err);
      message.error("خطا در تغییر وضعیت موتور");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchDailyStats(daysRange);
    fetchMonthlyStats();
  }, []);

  const totalNews = categoryStats.reduce((sum, item) => sum + item.count, 0);

  console.log("Rendering dailyStats:", dailyStats);
  console.log("Rendering monthlyStats:", monthlyStats);

  return (
    <div>
      <PageTitle title="صفحه اصلی داشبورد" />

      {/* --- خلاصه آمار --- */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="تعداد Sources"
              value={sourcesCount}
              valueStyle={{ color: "#3f8600" }}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="تعداد News"
              value={newsCount}
              valueStyle={{ color: "#1890ff" }}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="وضعیت Engine"
              value={engineRunning ? "در حال اجرا" : "متوقف"}
              valueStyle={{ color: engineRunning ? "#3f8600" : "#cf1322" }}
              prefix={<SyncOutlined spin={engineRunning} />}
            />
            <div style={{ marginTop: 16, textAlign: "center" }}>
              <Switch checked={engineRunning} onChange={toggleEngine} loading={loading} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* --- نمودار روزانه --- */}
      <Row style={{ marginTop: 32 }}>
        <Col span={24}>
          <Card
            title={
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>تعداد اخبار ثبت‌شده</span>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <span style={{ fontSize: 13, color: "#555" }}>
                    مجموع: <b>{summary.total}</b> | میانگین روزانه: <b>{summary.average}</b>
                  </span>
                  <Select
                    value={daysRange}
                    onChange={handleDaysChange}
                    options={[
                      { label: "۷ روز گذشته", value: 7 },
                      { label: "۳۰ روز گذشته", value: 30 },
                    ]}
                    style={{ width: 150 }}
                  />
                </div>
              </div>
            }
          >
            {loadingCharts ? (
              <Spin style={{ width: "100%", marginTop: 100 }} />
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={dailyStats} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => `${value} خبر`} />
                  <defs>
                    <linearGradient id="barColor" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1890ff" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#b3d9ff" stopOpacity={0.5} />
                    </linearGradient>
                  </defs>
                  <Bar dataKey="count" fill="url(#barColor)" radius={[6, 6, 0, 0]} animationDuration={800}>
                    {dailyStats.map((entry, index) => {
                      const max = Math.max(...dailyStats.map((d) => d.count));
                      const intensity = entry.count / max;
                      const color = `hsl(${220 - intensity * 40}, 100%, ${60 - intensity * 20}%)`;
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Col>
      </Row>

     {/* --- نمودار ماهانه --- */}
    <Row style={{ marginTop: 32 }}>
      <Col span={24}>
        <Card
          title={
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>آمار اخبار بر اساس ماه</span>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <span style={{ fontSize: 13, color: "#555" }}>
                  مجموع: <b>{monthlySummary.total}</b> | میانگین ماهانه: <b>{monthlySummary.average}</b>
                </span>
                <Select
                  value={monthsRange}
                  onChange={handleMonthsChange}
                  options={[
                    { label: "۶ ماه گذشته", value: 6 },
                    { label: "۱۲ ماه گذشته", value: 12 },
                  ]}
                  style={{ width: 150 }}
                />
              </div>
            </div>
          }
        >
          {loadingCharts ? (
            <Spin style={{ width: "100%", marginTop: 100 }} />
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={monthlyStats} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip formatter={(value) => `${value} خبر`} />
                <defs>
                    <linearGradient id="barColorMonthly" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#8e44ad" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#d3adf7" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <Bar dataKey="count" fill="url(#barColorMonthly)" radius={[6, 6, 0, 0]} animationDuration={800}>
                    {monthlyStats.map((entry, index) => {
                      const max = Math.max(...monthlyStats.map((d) => d.count));
                      const intensity = entry.count / max;
                      const color = `hsl(${260 - intensity * 50}, 80%, ${55 - intensity * 15}%)`;
                      return <Cell key={`cell-month-${index}`} fill={color} />;
                    })}
                  </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </Col>
    </Row>


      {/* --- نمودار دسته‌بندی --- */}
      <Row style={{ marginTop: 32 }}>
        <Col span={24}>
          <Card title="توزیع اخبار بر اساس دسته‌بندی">
            <ResponsiveContainer width="100%" height={350}>
              <PieChart>
                <Pie
                  data={categoryStats}
                  dataKey="count"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius="80%"
                  label={({ name, value }) => {
                    const percent = ((value / totalNews) * 100).toFixed(1);
                    return `${name}: ${value} (${percent}%)`;
                  }}
                >
                  {categoryStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `${value} خبر`} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Home;
